<template>
  <el-popover
    ref="popoverRef"
    placement="right"
    trigger="click"
    :show-arrow="false"
    :width="220"
    popper-class="c-app-menu-action-favorite__popover"
    @show="handlePopoverShow"
    @hide="handlePopoverHide"
  >
    <template #reference>
      <el-icon id="c-app-menu-favorite-action-icon">
        <StarFilled v-if="isFaved" color="#ffa400" />
        <Star v-else></Star>
      </el-icon>
    </template>
    <div v-if="isPopoverShow" class="c-app-menu-action-favorite__popover__panel">
      <div v-if="isFaved" class="to_unfav">
        <div class="message">
          <el-icon class="q-mr-sm">
            <WarningFilled color="#f90" />
          </el-icon>
          <span>你想要取消收藏？</span>
        </div>
        <div class="actions flex flex-end">
          <el-button size="mini" @click="hidePopover">再想想</el-button>
          <el-button type="primary" size="mini" @click="handleRemove">是的</el-button>
        </div>
      </div>
      <div v-else class="to_fav">
        <div class="panel__title">
          <div class="panel__label">添加到：</div>
          <div class="panel__extra">
            <el-button type="text" @click="handleConfirm">确定</el-button>
          </div>
        </div>
        <div class="panel__body">
          <el-radio-group v-model="selectedFolderId">
            <div class="radio__item" v-for="(it, index) in currentFolders" :key="`ri_${it.id}`">
              <el-radio :label="it.id">
                <div class="radio__label">{{ it.folderName }}</div>
              </el-radio>
            </div>
          </el-radio-group>
          <div v-if="isShowInput" class="radio__label">
            <div class="panel__editor">
              <el-input class="action__input" placeholder="新建文件夹" v-model="inputFolderName" />
              <el-icon class="action__icon" @click="handleAdd"><Check /></el-icon>
              <el-icon class="action__icon" @click="handleCancel"><Close /></el-icon>
            </div>
          </div>
        </div>
        <el-divider style="margin: 5px 0" />
        <div class="panel__footer">
          <el-button type="text" size="middle">
            <div class="panel__action flex-center" @click="handleNew">
              <el-icon><Plus /></el-icon>
              <span class="q-ml-md">新建分组</span>
            </div>
          </el-button>
        </div>
      </div>
    </div>
  </el-popover>
</template>

<script setup lang="ts">
import { _, ref, computed, reactive, provide, onMounted, nextTick, useHostAppInstance } from '@zto/zpage'
import { ElementPlusIcons } from '@zto/zpage-ui-element'

const { Star, StarFilled, Plus, Check, Close, WarningFilled } = ElementPlusIcons

import { favUtil } from '../../../../utils'

const props = withDefaults(
  defineProps<{
    menu?: any
  }>(),
  {}
)
const app = useHostAppInstance()

const { userStore } = app.stores

const popoverRef = ref<any>()

// 为了优化菜单性能，这里接入isPopoverShow减少渲染
const isPopoverShow = ref<boolean>(false)

const selectedFolderId = ref<number | null>(0)

const isShowInput = ref<boolean>(false)
const inputFolderName = ref<string>('')

/**
 * 当前所有菜单
 */
const currentFolders = ref<any[]>([])

const userFavorite = computed(() => {
  return userStore.data.favorite || {}
})

const existsFavs = computed(() => {
  return userFavorite.value.favs || []
})

const currentFav = computed(() => {
  return existsFavs.value.find(it => it.itemCode === props.menu?.bizId)
})

const isFaved = computed(() => {
  return !!currentFav.value
})

const selectedFolder = computed(() => {
  if (!selectedFolderId.value) return

  return currentFolders.value.find(it => it.id === selectedFolderId.value)
})

async function handlePopoverShow() {
  isPopoverShow.value = true

  await loadFolders()
}

function handlePopoverHide() {
  isPopoverShow.value = false
}

async function handleConfirm() {
  await doConfirm()

  hidePopover()
}

async function handleAdd() {
  const folderName = inputFolderName.value

  if (folderName) {
    await doAdd(folderName)

    await loadFolders()
  }

  isShowInput.value = false
  inputFolderName.value = ''
}

async function handleRemove() {
  await doRemove()
}

function handleNew() {
  inputFolderName.value = ''
  isShowInput.value = true
}

function handleCancel() {
  isShowInput.value = false
  inputFolderName.value = ''
}

async function doAdd(folderName: string) {
  favUtil.addFavFolder(app, -1, folderName)
}

async function doRemove() {
  if (!currentFav.value) return

  await app.request({
    api: 'workbench.pc.cancelCollection',
    params: {
      portalRoleCode: app.userBasicInfo.portalRoleCode,
      id: currentFav.value.id
    }
  })

  await loadFavCodes()

  app.ui.showMessage({
    type: 'success',
    message: '取消收藏成功'
  })

  nextTick(() => {
    hidePopover()
  })
}

async function doConfirm() {
  if (!selectedFolder.value || !props.menu) return

  await favUtil.addFavItem(app, selectedFolderId.value, props.menu)

  app.ui.showMessage({ type: 'success', message: '收藏成功' })

  nextTick(() => {
    hidePopover()
  })
}

function hidePopover() {
  isShowInput.value = false
  popoverRef.value?.hide()
}

async function loadFavCodes() {
  await favUtil.ensureFavs(app, true)
}

/**
 * 获取所有收藏夹
 */
async function loadFolders() {
  currentFolders.value = await favUtil.ensureFavFolders(app, true)
}
</script>

<style lang="scss">
.c-app-menu-action-favorite__popover {
  &.el-popover {
    padding: 12px 0px;
  }

  &__panel {
    .panel {
      &__title {
        display: flex;
        align-items: center;
        margin-bottom: 6px;
        padding: 0 12px;
      }

      &__label {
        flex: 1;
        color: #333;
        font-size: 16px;
        font-weight: 600;
        line-height: 22px;
      }

      &__body {
        padding: 0px 20px;

        .radio {
          &__label {
            font-size: 14px;
            font-weight: 400;
          }
        }
      }

      &__action {
        padding: 0px 12px;
        font-size: 14px;
      }

      &__editor {
        display: flex;
        align-items: center;
        color: var(--primary);
        margin-top: 5px;

        .action {
          &__input {
            margin-right: 5px;
          }

          &__icon {
            padding: 0 2px;
          }
        }
      }
    }

    .to_unfav {
      padding: 0px 10px;

      .message {
        display: flex;
        align-items: center;
        padding: 5px 10px;
      }

      .actions {
        padding: 10px;
        align-items: center;
        justify-content: flex-end;
      }
    }
  }
}
</style>
